import { lazy, Suspense, useState } from "react";
import { BorderBox, Button, GradientLine } from "../components";
import Spinner from "../components/Spinner";

const KnowledgeExplorer = lazy(() => import("../features/explore/KnowledgeExplorer"));

export default function Explore() {
  const [hasEntered, setHasEntered] = useState(false);

  if (!hasEntered) {
    return <ExploreIntro onContinue={() => setHasEntered(true)} />;
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] pb-4">
      <BorderBox noPadding className="w-full overflow-hidden">
        <div className="rke-scope h-full w-full overflow-y-auto no-scrollbar">
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center">
                <Spinner />
              </div>
            }
          >
            <KnowledgeExplorer />
          </Suspense>
        </div>
      </BorderBox>
    </div>
  );
}

function ExploreIntro({ onContinue }) {
  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] pb-4">
      <BorderBox noPadding className="flex flex-col">
        <div className="flex-1 overflow-y-auto no-scrollbar px-6 py-8 md:px-10">
          <div className="mx-auto flex max-w-3xl flex-col">
            <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
              <h1 className="text-4xl md:text-5xl text-lz-ink-mid dark:text-lz-concrete">
                Rosicrucian Knowledge Explorer
              </h1>
              <span className="rounded-full border border-lz-candle/40 bg-lz-candle/10 px-3 py-1 text-sm font-semibold uppercase tracking-wide text-lz-candle-dim dark:text-lz-candle">
                Beta
              </span>
            </div>

            <p className="mb-6 text-xl text-lz-ink-mid/80 dark:text-lz-concrete/80">
              Welcome to the Rosicrucian Knowledge Explorer.
            </p>

            <p className="text-lg text-lz-ink-mid dark:text-lz-concrete">
              This is more than a search engine. Rather than simply locating
              documents containing specific keywords, the Knowledge Explorer uses
              artificial intelligence to search across Rosicrucian materials and
              provide synthesized answers, insights, and summaries drawn from
              multiple sources.
            </p>

            <GradientLine className="my-8" />
            <h2 className="mb-3 text-2xl text-lz-ink-mid dark:text-lz-concrete">
              How to Use It
            </h2>
            <p className="mb-4 text-lg text-lz-ink-mid dark:text-lz-concrete">
              The Knowledge Explorer works best when you ask complete questions or
              write your query as a sentence.
            </p>
            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-lz-candle-dim dark:text-lz-candle">
              Examples
            </p>
            <ExampleList items={ANSWER_EXAMPLES} />

            <GradientLine className="my-8" />
            <h2 className="mb-3 text-2xl text-lz-ink-mid dark:text-lz-concrete">
              Finding Specific Documents
            </h2>
            <p className="mb-4 text-lg text-lz-ink-mid dark:text-lz-concrete">
              If you are looking for documents rather than answers, phrase your
              request as a sentence.
            </p>
            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-lz-candle-dim dark:text-lz-candle">
              Examples
            </p>
            <ExampleList items={DOCUMENT_EXAMPLES} />

            <GradientLine className="my-8" />
            <h2 className="mb-3 text-2xl text-lz-ink-mid dark:text-lz-concrete">
              Beta Notice
            </h2>
            <p className="text-lg text-lz-ink-mid dark:text-lz-concrete">
              The Knowledge Explorer is currently in beta testing. While it can
              access a wide range of Rosicrucian materials, its responses may
              occasionally include source documents that are only indirectly
              related to your question or of peripheral value. We encourage you to
              review the cited sources and experiment with different ways of
              phrasing your questions to achieve the best results.
            </p>
            <p className="mt-4 text-base italic text-lz-ink-mid/70 dark:text-lz-concrete/70">
              For the best experience, you will want to use a computer rather than
              a mobile device.
            </p>
          </div>
        </div>

        <div className="flex justify-center border-t border-lz-paper-border px-6 py-4 dark:border-lz-charcoal-border md:justify-end md:px-10">
          <Button withArrow className="w-fit uppercase" onClick={onContinue}>
            Continue to Rosicrucian Knowledge Explorer
          </Button>
        </div>
      </BorderBox>
    </div>
  );
}

function ExampleList({ items }) {
  return (
    <ul className="flex flex-col gap-y-2">
      {items.map((item) => (
        <li
          key={item}
          className="rounded-lg border-l-2 border-lz-candle bg-lz-concrete px-4 py-3 italic text-lz-ink-mid dark:border-lz-candle-dim dark:bg-lz-ink-mid dark:text-lz-concrete"
        >
          {item}
        </li>
      ))}
    </ul>
  );
}

const ANSWER_EXAMPLES = [
  "What do Rosicrucian teachings say about reincarnation?",
  "How does meditation help develop intuition?",
  "What is the Rosicrucian view of free will?",
];

const DOCUMENT_EXAMPLES = [
  "Find a document in the archive that talks about reincarnation.",
  "Show me articles discussing spiritual alchemy.",
  "Find references to the Law of Karma.",
];
